import { useAppDispatch } from '@store/hooks';
import {
  selectModalClosingClass,
  selectModalData,
  selectModalIsAnimated,
  selectModalIsAnyOpen,
  selectModalIsOpen,
  selectModalMode,
  selectModalNotificationMode,
} from '@store/modal/selectors';
import { onModalCloseRequest } from '@store/modal/thunks';
import { isDefAndNotNull } from '@utils/def';
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import ReactDOM from 'react-dom';
import { useSelector } from 'react-redux';

import Button, { StyleTypes } from '../Button';
import { ICONS_TYPES } from '../Icon';

import ModalContext from './context';
import { Modes, NotificationModes, Sizes } from './enums';
import {
  ActionsContainer,
  Body,
  CloseIcon,
  ConfirmButton,
  ConfirmButtonsContainer,
  ConfirmContainer,
  ConfirmText,
  Content,
  Header,
  NotificationContainer,
  NotificationIcon,
  NotificationIconContainer,
  NotificationLabel,
  NotificationText,
  Root,
  Title,
} from './styles';
import { ModalDataType, Props } from './types';

const Modal = <TData extends ModalDataType>({
  boardedHeader = false,
  className = '',
  confirm,
  confirmButtonProps,
  enableCloseByClickOutside = true,
  hideConfirmButton,
  hideXMark = false,
  id,
  onClose,
  overflowY = 'auto',
  renderActions,
  renderChildren,
  renderTitle,
  size = Sizes.small,
  notification,
  stickyHeader = true,
}: Props<TData>) => {
  const dispatch = useAppDispatch();
  const contentRef = useRef<HTMLDivElement | null>(null);
  const isOpen = useSelector(selectModalIsOpen(id));
  const data = useSelector(selectModalData(id)) as TData;
  const mode = useSelector(selectModalMode(id));
  const animated = useSelector(selectModalIsAnimated(id));
  const closingClass = useSelector(selectModalClosingClass(id));
  const isAnyOpen = useSelector(selectModalIsAnyOpen);
  const notificationMode = useSelector(selectModalNotificationMode(id));
  const [startClosing, setStartClosing] = useState(false);
  const notificationLabel = useMemo(
    () =>
      notificationMode === NotificationModes.success
        ? 'Success'
        : notificationMode === NotificationModes.error
          ? 'Error'
          : 'Warning',
    [notificationMode],
  );
  const notificationIcon = useMemo(
    () =>
      notificationMode === NotificationModes.success
        ? ICONS_TYPES.CheckMarkCircle
        : notificationMode === NotificationModes.error
          ? ICONS_TYPES.Error
          : ICONS_TYPES.Warning,
    [notificationMode],
  );

  const handleCloseModal = useCallback(() => {
    dispatch(onModalCloseRequest({ callBack: onClose, id }));
  }, [dispatch, id, onClose]);
  const handleClickOutside = useCallback(
    (event: React.MouseEvent<HTMLDivElement, MouseEvent>) => {
      if (
        event.target === event.currentTarget &&
        startClosing &&
        enableCloseByClickOutside
      ) {
        handleCloseModal();
      }
    },
    [enableCloseByClickOutside, handleCloseModal, startClosing],
  );
  const handleMouseDown = useCallback(
    (event: React.MouseEvent<HTMLDivElement, MouseEvent>) => {
      setStartClosing(event.target === event.currentTarget);
    },
    [setStartClosing],
  );
  const handleKeyDown = useCallback(
    (event: KeyboardEvent) => {
      if (event.key === 'Escape' && enableCloseByClickOutside) {
        handleCloseModal();
      }
    },
    [enableCloseByClickOutside, handleCloseModal],
  );

  useEffect(() => {
    document.body.style.overflow = isAnyOpen ? 'hidden' : 'visible';
  }, [isAnyOpen]);

  useEffect(() => {
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [handleKeyDown]);

  useEffect(() => {
    contentRef.current?.focus();
  }, [isOpen]);

  if (!isOpen) return null;

  const root = (
    <Root
      animated={animated}
      className={`${className}${closingClass}`}
      mode={mode}
      onMouseDown={handleMouseDown}
      onClick={handleClickOutside}>
      <Content
        animated={animated}
        className={closingClass}
        ref={contentRef}
        size={size}
        mode={mode}
        tabIndex={0}
        overflowY={overflowY}>
        <ModalContext.Provider value={{ modalContentRootRef: contentRef }}>
          {(!hideXMark || isDefAndNotNull(renderTitle)) && (
            <Header stickyHeader={stickyHeader} boardedHeader={boardedHeader}>
              {isDefAndNotNull(renderTitle) && (
                <Title>{renderTitle(data)}</Title>
              )}
              <ActionsContainer>
                {isDefAndNotNull(renderActions) && renderActions(data)}
                {!hideXMark && (
                  <CloseIcon
                    onClick={handleCloseModal}
                    icon={ICONS_TYPES.XMark}
                    size={20}
                    tabIndex={0}
                  />
                )}
              </ActionsContainer>
            </Header>
          )}
          <Body>
            {confirm ? (
              <ConfirmContainer>
                <ConfirmText>{renderChildren(data)}</ConfirmText>
                <ConfirmButtonsContainer>
                  {!hideConfirmButton && (
                    <ConfirmButton {...confirmButtonProps?.(data)} />
                  )}
                  <Button
                    label="Cancel"
                    onClick={handleCloseModal}
                    styleType={StyleTypes.outlined}
                    size={confirmButtonProps?.(data)?.size}
                  />
                </ConfirmButtonsContainer>
              </ConfirmContainer>
            ) : notification ? (
              <NotificationContainer>
                <NotificationIconContainer>
                  <NotificationIcon
                    icon={notificationIcon}
                    size={60}
                    notificationMode={notificationMode}
                  />
                  <NotificationLabel>{notificationLabel}!</NotificationLabel>
                </NotificationIconContainer>
                {data ? (
                  <NotificationText>{String(data)}</NotificationText>
                ) : (
                  <NotificationText>{renderChildren(data)}</NotificationText>
                )}
              </NotificationContainer>
            ) : (
              renderChildren(data)
            )}
          </Body>
        </ModalContext.Provider>
      </Content>
    </Root>
  );

  return ReactDOM.createPortal(root, document.body);
};

export type { Props };
export { Sizes, Modes, NotificationModes };

export default Modal;
